import { decamelize } from "humps";
import {
    convertTimeUnit,
    doNothing,
    formatNumber,
    formatUnderscoreName,
    unreachableCaseError,
} from "@edgetier/utilities";
import { IBooleanMetric, InteractionMetric, INumericMetric, ITimeMetric } from "@edgetier/watchtower-types";
import { useMemo } from "react";
import { isTimeMetric } from "utilities-for/metrics/is-time-metric";
import { isBooleanMetric } from "utilities-for/metrics/is-boolean-metric";

/**
 * Give a metric, find a nicer label, appropriate units to display and a function to convert values into the best unit
 * to display. This complicated because different metrics behave differently. For example, all the time metrics like
 * handling time, queue time etc., could either be displayed in seconds or minutes depending on the input data. This
 * hook works out the best unit to use.
 * @param metric      Metric to label or provide a conversion function.
 * @param configuration.isAggregate Some metrics are aggregated and it affects the result (for example, either the raw
 *                                  customer satisfaction form values could be being displayed or the "CSAT Score"
 *                                  which is derived from a formula from the values, with different labels and units).
 * @param configuration.items       Data for the metric.
 * @param configuration.getValue    Function to retrieve a value from the data.
 * @returns Axis and metric labels, the unit, and a function to format values.
 */
export const useInteractionMetricConversion = <T extends {}>(
    metric: InteractionMetric,
    configuration?: Partial<{ items: T[]; isAggregate: boolean; getValue: (item: T) => any }>
) => {
    const { getValue = doNothing, items = [], isAggregate = false } = configuration ?? {};
    return useMemo(() => {
        if (isTimeMetric(metric)) {
            const [metricLabel, unit, formatTick] = getTimeMetricConversion(metric, items, getValue);
            const axisLabel = typeof unit === "string" ? `${metricLabel} (${unit})` : metricLabel;
            const unitShorthand = typeof unit === "string" ? unit.charAt(0) : null;
            return { axisLabel, metricLabel, formatTick, unit, unitShorthand };
        }

        if (isBooleanMetric(metric)) {
            const [metricLabel, unit, formatTick] = getBooleanMetricConversion(metric, isAggregate);
            const axisLabel = typeof unit === "string" ? `${metricLabel} (${unit})` : metricLabel;
            const unitShorthand = typeof unit === "string" ? unit.charAt(0) : null;
            return { axisLabel, metricLabel, formatTick, unit, unitShorthand };
        }

        const [metricLabel, unit, formatTick] = getNumericMetricConversion(metric, isAggregate);
        const axisLabel = typeof unit === "string" ? `${metricLabel} (${unit})` : metricLabel;
        const unitShorthand = typeof unit === "string" ? unit.charAt(0) : null;
        return { axisLabel, metricLabel, formatTick, unit, unitShorthand };
    }, [getValue, isAggregate, items, metric]);
};

/**
 * Get conversion data for time metrics.
 * @param metric   A time metric.
 * @param items    Data for the metric
 * @param getValue Function to get a value from the data.
 * @returns The metric label, unit and conversion function for time metrics.
 */
const getTimeMetricConversion = <T extends {}>(
    metric: ITimeMetric,
    items: T[],
    getValue: (item: T) => number
): [metricLabel: string, unit: string | null, convert: (value: number) => string] => {
    // The maximum value will determine the best unit to use.
    const maximumValue = Math.max(...items.map(getValue));
    const [unit, formatFunction] = convertTimeUnit(maximumValue);
    switch (metric) {
        case InteractionMetric.FirstResponseTime:
            return ["First Response Time", unit, formatFunction];
        case InteractionMetric.HandlingTime:
            return ["Handling Time", unit, formatFunction];
        case InteractionMetric.AverageResponseTime:
            return ["Average Response Time", unit, formatFunction];
        case InteractionMetric.QueueTime:
            return ["Queue Time", unit, formatFunction];
        case InteractionMetric.AgentFirstResponseTime:
            return ["Agent First Response Time", unit, formatFunction];
        default:
            unreachableCaseError(metric);
            return [formatUnderscoreName(decamelize(metric)), null, (value) => formatNumber(value ?? 0)];
    }
};

/**
 * Get conversion data for boolean metrics.
 * @param metric      A boolean metric.
 * @param isAggregate A boolean indicating if the metric is an aggregate or not.
 * @returns The metric label, unit and conversion function for boolean metrics.
 */
function getBooleanMetricConversion(
    metric: IBooleanMetric,
    isAggregate: true
): [metricLabel: string, unit: string, convert: (value: number, options?: Intl.NumberFormatOptions) => string];
function getBooleanMetricConversion(
    metric: IBooleanMetric,
    isAggregate: false
): [metricLabel: string, unit: null, convert: (value: boolean) => string];
function getBooleanMetricConversion(
    metric: IBooleanMetric,
    isAggregate: boolean
): [
    metricLabel: string,
    unit: string | null,
    convert: (value: number | boolean, options?: Intl.NumberFormatOptions) => string
];
function getBooleanMetricConversion(metric: IBooleanMetric, isAggregate: boolean) {
    switch (metric) {
        case InteractionMetric.QueryResolution:
            return [
                isAggregate ? "Query Resolution Rate" : "Query Resolved",
                isAggregate ? "%" : null,
                isAggregate
                    ? (value: number, options?: Intl.NumberFormatOptions) =>
                          formatNumber(isAggregate ? 100 * (value ?? 0) : value ?? 0, options)
                    : (value: boolean): string => (value ? "Resolved" : "Not Resolved"),
            ];
        default:
            unreachableCaseError(metric);
            return [formatUnderscoreName(decamelize(metric)), null, (value: any) => formatNumber(value ?? 0)];
    }
}

/**
 * Get conversion data for numeric metrics.
 * @param metric   A numeric metric.
 * @param items    Data for the metric
 * @param getValue Function to get a value from the data.
 * @returns The metric label, unit and conversion function for numeric metrics.
 */
const getNumericMetricConversion = (
    metric: INumericMetric,
    isAggregate: boolean
): [
    metricLabel: string,
    unit: string | null,
    convert: (value: number, options?: Intl.NumberFormatOptions) => string
] => {
    switch (metric) {
        case InteractionMetric.CustomerSatisfaction:
            return [
                isAggregate ? "Customer Satisfaction Score" : "CSAT Rating",
                isAggregate ? "%" : null,
                (value, options) => formatNumber(isAggregate ? 100 * (value ?? 0) : value ?? 0, options),
            ];

        case InteractionMetric.NetPromoterScore:
            return [
                isAggregate ? "Net Promoter Score" : "NPS Rating",
                null,
                (value: number) => formatNumber(value ?? 0),
            ];
        case InteractionMetric.ExperienceScore:
            return [
                isAggregate ? "Experience Score" : "Experience Score",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        case InteractionMetric.AgentMessagesCount:
            return [
                isAggregate ? "Average Agent Messages Count" : "Agent Messages Count",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        case InteractionMetric.BotMessagesCount:
            return [
                isAggregate ? "Average Bot Messages Count" : "Bot Messages Count",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        case InteractionMetric.CustomerMessagesCount:
            return [
                isAggregate ? "Average Customer Messages Count" : "Customer Messages Count",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        case InteractionMetric.MessagesCount:
            return [
                isAggregate ? "Average Messages Count" : "All Messages Count",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        case InteractionMetric.ServiceLevelAgreement:
            return [
                isAggregate ? "Service Level Agreement" : "Service Level Agreement",
                "%",
                (value, options) => formatNumber(isAggregate ? 100 * (value ?? 0) : value ?? 0, options),
            ];
        case InteractionMetric.AgentSatisfaction:
            return [
                isAggregate ? "Agent Satisfaction" : "Agent Satisfaction",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        case InteractionMetric.AgentScore:
            return [
                isAggregate ? "Agent Score" : "Agent Score",
                null,
                (value, options) => formatNumber(value ?? 0, options),
            ];
        default:
            unreachableCaseError(metric);
            return [formatUnderscoreName(decamelize(metric)), null, (value) => formatNumber(value ?? 0)];
    }
};
